import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AchievementsContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: var(--color-dark);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled(motion.h1)`
  color: var(--color-white);
  font-size: 3rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: var(--color-primary);
    border-radius: 3px;
  }
`;

const AchievementsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 3rem;
`;

const AchievementCard = styled(motion.div)<{ unlocked: boolean }>`
  background: rgba(26, 26, 26, 0.7);
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  border: 1px solid ${props => props.unlocked ? 'var(--color-primary)' : 'rgba(255, 144, 82, 0.1)'};
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;
  opacity: ${props => props.unlocked ? 1 : 0.7};
  filter: ${props => props.unlocked ? 'none' : 'grayscale(70%)'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${props => props.unlocked ? 'radial-gradient(circle at center, rgba(255, 144, 82, 0.1) 0%, rgba(0, 0, 0, 0) 70%)' : 'none'};
    z-index: 0;
  }
`;

const AchievementIcon = styled.div<{ unlocked: boolean }>`
  font-size: 4rem;
  color: ${props => props.unlocked ? 'var(--color-primary)' : 'var(--color-white)'};
  margin-bottom: 1.5rem;
  z-index: 1;
  filter: ${props => props.unlocked ? 'drop-shadow(0 0 10px rgba(255, 144, 82, 0.5))' : 'none'};
`;

const AchievementTitle = styled.h3<{ unlocked: boolean }>`
  color: ${props => props.unlocked ? 'var(--color-primary)' : 'var(--color-white)'};
  font-size: 1.5rem;
  margin-bottom: 1rem;
  text-align: center;
  z-index: 1;
`;

const AchievementDescription = styled.p`
  color: var(--color-white);
  opacity: 0.7;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  z-index: 1;
`;

const AchievementPoints = styled.div<{ unlocked: boolean }>`
  background: ${props => props.unlocked ? 'rgba(255, 144, 82, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.unlocked ? 'var(--color-primary)' : 'var(--color-white)'};
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 1;
  border: 1px solid ${props => props.unlocked ? 'rgba(255, 144, 82, 0.3)' : 'transparent'};
`;

const AchievementBadge = styled.div<{ unlocked: boolean }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => props.unlocked ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.1)'};
  color: var(--color-white);
  padding: 0.3rem 0.8rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: bold;
  z-index: 2;
  box-shadow: ${props => props.unlocked ? '0 0 10px rgba(255, 144, 82, 0.3)' : 'none'};
`;

const ProgressContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin-bottom: 3rem;
  background: rgba(26, 26, 26, 0.7);
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 144, 82, 0.2);
  backdrop-filter: blur(10px);
`;

const ProgressTitle = styled.h2`
  color: var(--color-white);
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%;
  background: var(--color-primary);
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(255, 144, 82, 0.3);
`;

const ProgressStats = styled.div`
  display: flex;
  justify-content: space-between;
  color: var(--color-white);
  opacity: 0.7;
`;

const ProgressLevel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1.5rem;
`;

const LevelBadge = styled.div`
  background: var(--color-primary);
  color: var(--color-white);
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: bold;
  box-shadow: 0 0 15px rgba(255, 144, 82, 0.3);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BackButton = styled(motion.button)`
  background: rgba(26, 26, 26, 0.7);
  color: var(--color-white);
  font-size: 1rem;
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  border: 2px solid var(--color-primary);
  margin-top: 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 15px rgba(255, 144, 82, 0.2);
  backdrop-filter: blur(5px);
  
  &:hover {
    background: rgba(255, 144, 82, 0.2);
  }
`;

const AchievementsSection: React.FC<{ onBack: () => void }> = ({ onBack }) => {
 const achievements = [
  {
    icon: "🏆",
    title: "Master Architect",
    description: "Designed and implemented complex, scalable software architectures.",
    points: 500,
    unlocked: true,
    category: "Professional"
  },
  {
    icon: "🎮",
    title: "Game Dev Expert",
    description: "Developed games using multiple engines and technologies.",
    points: 400,
    unlocked: true,
    category: "Technical"
  },
  {
    icon: "👨‍💻",
    title: "Tech Lead",
    description: "Led technical teams in large-scale projects.",
    points: 450,
    unlocked: true,
    category: "Leadership"
  },
  {
    icon: "🚀",
    title: "Innovator",
    description: "Implemented innovative solutions transforming processes and products.",
    points: 350,
    unlocked: true,
    category: "Innovation"
  },
  {
    icon: "🌐",
    title: "Full Stack Master",
    description: "Mastered front-end and back-end technologies to create comprehensive applications.",
    points: 300,
    unlocked: true,
    category: "Technical"
  },
  {
    icon: "🔧",
    title: "Optimizer",
    description: "Significantly improved performance of existing systems.",
    points: 250,
    unlocked: true,
    category: "Technical"
  },
  {
    icon: "🧠",
    title: "Critical Thinker",
    description: "Applied critical thinking to solve complex problems.",
    points: 200,
    unlocked: true,
    category: "Soft Skill"
  },
  {
    icon: "❤️",
    title: "Humanized Manager",
    description: "Implemented people-centered management practices focused on wellbeing.",
    points: 350,
    unlocked: true,
    category: "Leadership"
  },
  {
    icon: "🔐",
    title: "Security Guardian",
    description: "Implemented robust security practices in critical systems.",
    points: 300,
    unlocked: true,
    category: "Technical"
  },
  {
    icon: "🌟",
    title: "Visionary",
    description: "Defined strategic visions guiding teams and products.",
    points: 400,
    unlocked: true,
    category: "Leadership"
  },
  {
    icon: "📱",
    title: "Mobile Master",
    description: "Developed high-quality, high-performance mobile applications.",
    points: 250,
    unlocked: true,
    category: "Technical"
  },
  {
    icon: "🎓",
    title: "Mentor",
    description: "Guided and developed junior and mid-level professionals.",
    points: 300,
    unlocked: true,
    category: "Leadership"
  },
  {
    icon: "📦",
    title: "Microservices Expert",
    description: "Built robust microservices architectures to enhance system scalability and reliability.",
    points: 350,
    unlocked: true,
    category: "Technical"
  },
  {
    icon: "🛠️",
    title: "DevOps Practitioner",
    description: "Integrated continuous deployment and infrastructure as code practices to streamline workflows.",
    points: 300,
    unlocked: true,
    category: "Technical"
  },
  {
    icon: "🔄",
    title: "Migration Specialist",
    description: "Successfully migrated legacy systems to modern, scalable architectures.",
    points: 350,
    unlocked: true,
    category: "Professional"
  }
];

  
  const unlockedAchievements = achievements.filter(a => a.unlocked);
  const totalPoints = unlockedAchievements.reduce((sum, a) => sum + a.points, 0);
  const maxPoints = achievements.reduce((sum, a) => sum + a.points, 0);
  const progressPercentage = (totalPoints / maxPoints) * 100;
  
  // Determine level based on points
  const getLevel = (points: number) => {
    if (points >= 3000) return { level: "Lendário", icon: "👑" };
    if (points >= 2500) return { level: "Mestre", icon: "⭐" };
    if (points >= 2000) return { level: "Expert", icon: "🔥" };
    if (points >= 1500) return { level: "Profissional", icon: "💎" };
    if (points >= 1000) return { level: "Avançado", icon: "🏅" };
    if (points >= 500) return { level: "Intermediário", icon: "🥈" };
    return { level: "Iniciante", icon: "🥉" };
  };
  
  const currentLevel = getLevel(totalPoints);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 100, 
        damping: 12
      }
    }
  };

  return (
    <AchievementsContainer>
      <Title
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        Achievements
      </Title>
      
      <ProgressContainer
        as={motion.div}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
      >
        <ProgressTitle>Seu Progresso</ProgressTitle>
        <ProgressBar>
          <ProgressFill 
            progress={progressPercentage}
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          />
        </ProgressBar>
        <ProgressStats>
          <div>{unlockedAchievements.length} of {achievements.length} achievements</div>
          <div>{totalPoints} of {maxPoints} points</div>
        </ProgressStats>
        <ProgressLevel>
          <LevelBadge>
            <span>{currentLevel.icon}</span>
            Level: {currentLevel.level}
          </LevelBadge>
        </ProgressLevel>
      </ProgressContainer>
      
      <AchievementsGrid
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {achievements.map((achievement, index) => (
          <AchievementCard
            key={index}
            unlocked={achievement.unlocked}
            variants={itemVariants}
          >
            <AchievementBadge unlocked={achievement.unlocked}>
              {achievement.unlocked ? "Unlocked" : "Locked"}
            </AchievementBadge>
            <AchievementIcon unlocked={achievement.unlocked}>
              {achievement.icon}
            </AchievementIcon>
            <AchievementTitle unlocked={achievement.unlocked}>
              {achievement.title}
            </AchievementTitle>
            <AchievementDescription>
              {achievement.description}
            </AchievementDescription>
            <AchievementPoints unlocked={achievement.unlocked}>
              <span>🔶</span>
              {achievement.points} points
            </AchievementPoints>
          </AchievementCard>
        ))}
      </AchievementsGrid>
      
      <BackButton 
        onClick={onBack}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Back to Menu
      </BackButton>
    </AchievementsContainer>
  );
};

export default AchievementsSection;
